/**
 * CouponPerformancePanel — the analytics panel above the promotion hero.
 *
 * Replaces the old flat 4-box stat strip with something that answers "is this
 * coupon working, and WHEN does it get used?" at a glance:
 *   • a headline number for the selected metric + a 30-day trend chip
 *   • an area "stock chart" of daily usage (toggle uses ⇄ revenue)
 *   • a compact stat rail: uses-left, avg order value, and a new-vs-repeat
 *     split bar (visual, not just "76 · 24")
 *
 * Data from usePricingRulePerformance (real Sales Orders + daily time-series).
 * Shown only for coupon rules; automatic rules render nothing.
 */

import { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CalendarClock,
  Package,
  Receipt,
  Sparkles,
  Ticket,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import { formatMoney } from "@/features/customers/customer-detail/customerDetailPrimitives";
import { useLanguage } from "@/i18n/useLanguage";
import { isCouponRule, ruleCurrency } from "./pricingRuleDetailModel";
import {
  usePricingRulePerformance,
  type CouponTimePoint,
  type CouponRoi,
  type CouponTiming,
  type CouponTopProduct,
} from "./usePricingRulePerformance";

const DOW_TH = ["จ", "อ", "พ", "พฤ", "ศ", "ส", "อา"];
const DOW_EN = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

type Metric = "uses" | "revenue";

const pick = (th: boolean, t: string, e: string) => (th ? t : e);

const ACCENT = "#0ea5e9"; // sky-500 — brand accent

/** Trim leading empty days so the chart stays dense, keeping a min window. */
function chartWindow(ts: CouponTimePoint[], minDays = 14): CouponTimePoint[] {
  if (!ts.length) return [];
  const firstIdx = ts.findIndex((p) => p.uses > 0 || p.revenue > 0);
  if (firstIdx < 0) return ts.slice(-Math.min(30, ts.length)); // no activity yet
  const padStart = Math.max(0, firstIdx - 2);
  const minStart = Math.max(0, ts.length - minDays);
  return ts.slice(Math.min(padStart, minStart));
}

/** Percent change vs the prior period. null = "new" (no prior baseline). */
function trendPct(recent: number, prev: number): number | null {
  if (prev <= 0) return recent > 0 ? null : 0;
  return ((recent - prev) / prev) * 100;
}

function shortDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

function TrendChip({
  recent,
  prev,
  th,
}: {
  recent: number;
  prev: number;
  th: boolean;
}) {
  const pct = trendPct(recent, prev);
  if (pct === null) {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full bg-sky-50 px-1.5 py-0.5 text-[11px] font-semibold text-sky-600 dark:bg-sky-500/10 dark:text-sky-400">
        <TrendingUp className="h-3 w-3" />
        {pick(th, "เริ่มมีการใช้", "new activity")}
      </span>
    );
  }
  if (Math.abs(pct) < 0.5) {
    return (
      <span className="text-[11px] font-medium text-gray-400">
        {pick(th, "ทรงตัว", "flat")}
      </span>
    );
  }
  const up = pct > 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${
        up
          ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400"
          : "bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400"
      }`}
    >
      <Icon className="h-3 w-3" />
      {up ? "+" : ""}
      {pct.toFixed(0)}%
    </span>
  );
}

/** New-customer value + ROI (or perk framing) — two compact insight callouts. */
function InsightCallouts({
  th,
  money,
  newRevenue,
  newRevenuePct,
  roi,
  revenue,
  avgOrder,
}: {
  th: boolean;
  money: (n: number) => string;
  newRevenue: number;
  newRevenuePct: number;
  roi: CouponRoi;
  revenue: number;
  avgOrder: number;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {/* New-customer revenue */}
      <div className="rounded-lg border border-border/60 bg-muted/30 p-3 dark:bg-white/[0.03]">
        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <Users className="h-3.5 w-3.5" />
          {pick(th, "ยอดจากลูกค้าใหม่", "New-customer revenue")}
        </div>
        <div className="mt-1 text-lg font-bold tabular-nums text-gray-900 dark:text-gray-100">
          {money(newRevenue)}
        </div>
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted dark:bg-white/[0.08]">
          <div className="h-full rounded-full bg-sky-500" style={{ width: `${newRevenuePct}%` }} />
        </div>
        <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
          {newRevenuePct}% {pick(th, "ของยอดขายคูปอง", "of coupon revenue")}
        </div>
      </div>

      {/* ROI (real ratio for discount coupons; perk framing otherwise) */}
      <div className="rounded-lg border border-border/60 bg-muted/30 p-3 dark:bg-white/[0.03]">
        {roi.cost_type === "discount" && roi.return_x != null ? (
          <>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <Sparkles className="h-3.5 w-3.5" />
              {pick(th, "ความคุ้มค่า", "Return on cost")}
            </div>
            <div className="mt-1 text-lg font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
              {roi.return_x}x
            </div>
            <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              {pick(
                th,
                `ให้ส่วนลด ${money(roi.cost ?? 0)} → ได้ ${money(revenue)}`,
                `gave ${money(roi.cost ?? 0)} → earned ${money(revenue)}`,
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <Sparkles className="h-3.5 w-3.5" />
              {pick(th, "แลกด้วยสิทธิพิเศษ", "Perk-based")}
            </div>
            <div className="mt-1 text-lg font-bold tabular-nums text-gray-900 dark:text-gray-100">
              {money(avgOrder)}
            </div>
            <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              {pick(th, "ยอดขายเฉลี่ยต่อการใช้ 1 ครั้ง", "revenue per redemption")}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Horizontal bar list of the products the coupon moves most (by qty). */
function TopProducts({ th, items }: { th: boolean; items: CouponTopProduct[] }) {
  if (!items.length) return null;
  const max = Math.max(...items.map((i) => i.qty), 1);
  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
        <Package className="h-3.5 w-3.5" />
        {pick(th, "สินค้าขายดีจากคูปอง", "Top products")}
      </div>
      <ul className="space-y-1.5">
        {items.map((p) => (
          <li key={p.item_code} className="flex items-center gap-2 text-xs">
            <span className="w-28 shrink-0 truncate text-gray-700 dark:text-gray-300" title={p.item_name}>
              {p.item_name}
            </span>
            <span className="relative h-2 flex-1 overflow-hidden rounded-full bg-muted dark:bg-white/[0.08]">
              <span
                className="absolute inset-y-0 left-0 rounded-full bg-sky-400/70"
                style={{ width: `${(p.qty / max) * 100}%` }}
              />
            </span>
            <span className="w-9 shrink-0 text-right font-semibold tabular-nums text-gray-900 dark:text-gray-100">
              {p.qty.toLocaleString()}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Day-of-week mini bar chart + last-used / peak-day caption. */
function TimingBars({ th, timing }: { th: boolean; timing: CouponTiming }) {
  const labels = th ? DOW_TH : DOW_EN;
  const max = Math.max(...timing.dow, 1);
  const peakIdx = timing.dow.indexOf(Math.max(...timing.dow));
  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
        <CalendarClock className="h-3.5 w-3.5" />
        {pick(th, "ช่วงเวลาที่ใช้", "When it's used")}
      </div>
      <div className="flex items-end gap-1.5" style={{ height: 52 }}>
        {timing.dow.map((c, i) => (
          <div key={i} className="flex flex-1 flex-col items-center gap-1">
            <div className="flex w-full flex-1 items-end">
              <div
                className={`w-full rounded-t ${
                  i === peakIdx && c > 0 ? "bg-sky-500" : "bg-sky-400/40"
                }`}
                style={{ height: `${(c / max) * 100}%`, minHeight: c > 0 ? 4 : 0 }}
              />
            </div>
            <span className="text-[10px] text-gray-400">{labels[i]}</span>
          </div>
        ))}
      </div>
      <div className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
        {timing.last_used
          ? pick(
              th,
              `ใช้ล่าสุด ${shortDate(timing.last_used)} · พีค ${
                timing.peak_date ? shortDate(timing.peak_date) : "-"
              } (${timing.peak_uses} ครั้ง)`,
              `last ${shortDate(timing.last_used)} · peak ${
                timing.peak_date ? shortDate(timing.peak_date) : "-"
              } (${timing.peak_uses})`,
            )
          : pick(th, "ยังไม่มีการใช้", "no usage yet")}
      </div>
    </div>
  );
}

export function CouponPerformancePanel({
  formData,
  docName,
}: {
  formData: Record<string, any>;
  docName: string;
}) {
  const { language } = useLanguage();
  const th = language === "th";
  const coupon = isCouponRule(formData);
  const { data, isLoading } = usePricingRulePerformance(docName, coupon && !!docName);
  const [metric, setMetric] = useState<Metric>("uses");

  const currency = data?.currency || ruleCurrency(formData);
  const money = (n: number) => formatMoney(n, currency);

  const series = useMemo(() => chartWindow(data?.timeseries ?? []), [data?.timeseries]);
  const hasActivity = (data?.redemptions ?? 0) > 0;

  // Only coupon rules get the panel — automatic rules have no coupon usage.
  if (!coupon) return null;

  if (isLoading) {
    return (
      <div className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5">
        <div className="h-5 w-40 animate-pulse rounded bg-muted/60" />
        <div className="mt-3 h-8 w-32 animate-pulse rounded bg-muted/60" />
        <div className="mt-4 h-[150px] animate-pulse rounded-lg bg-muted/40" />
      </div>
    );
  }

  const redemptions = data?.redemptions ?? 0;
  const revenue = data?.revenue ?? 0;
  const usesLeft = data?.uses_left ?? null;
  const cust = data?.customers ?? {
    total: 0,
    new: 0,
    repeat: 0,
    new_revenue: 0,
    new_revenue_pct: 0,
  };
  const topProducts = data?.top_products ?? [];
  const timing = data?.timing ?? {
    dow: [0, 0, 0, 0, 0, 0, 0],
    last_used: null,
    peak_date: null,
    peak_uses: 0,
  };
  const roi = data?.roi ?? { cost: null, cost_type: "perk" as const, return_x: null };
  const trend = data?.trend ?? {
    uses_30d: 0,
    uses_prev_30d: 0,
    revenue_30d: 0,
    revenue_prev_30d: 0,
  };
  const avgOrder = redemptions > 0 ? revenue / redemptions : 0;

  const headlineValue =
    metric === "uses" ? redemptions.toLocaleString() : money(revenue);
  const headlineUnit = metric === "uses" ? pick(th, "ครั้ง", "uses") : "";
  const trendRecent = metric === "uses" ? trend.uses_30d : trend.revenue_30d;
  const trendPrev = metric === "uses" ? trend.uses_prev_30d : trend.revenue_prev_30d;

  const newPct = cust.total > 0 ? Math.round((cust.new / cust.total) * 100) : 0;
  const repeatPct = cust.total > 0 ? 100 - newPct : 0;

  const ToggleBtn = ({ m, label }: { m: Metric; label: string }) => (
    <button
      type="button"
      onClick={() => setMetric(m)}
      className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
        metric === m
          ? "bg-white text-gray-900 shadow-sm dark:bg-white/10 dark:text-gray-100"
          : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5">
      {/* Header: title + metric toggle */}
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {pick(th, "ผลงานคูปอง", "Coupon performance")}
        </h3>
        <div className="inline-flex rounded-lg bg-muted/60 p-0.5 dark:bg-white/[0.06]">
          <ToggleBtn m="uses" label={pick(th, "ครั้งที่ใช้", "Uses")} />
          <ToggleBtn m="revenue" label={pick(th, "ยอดขาย", "Revenue")} />
        </div>
      </div>

      {/* Headline number + trend chip */}
      <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-[28px] font-bold leading-none tabular-nums text-gray-900 dark:text-gray-100">
          {headlineValue}
        </span>
        {headlineUnit && (
          <span className="text-sm text-gray-500 dark:text-gray-400">{headlineUnit}</span>
        )}
        <TrendChip recent={trendRecent} prev={trendPrev} th={th} />
        <span className="ml-auto text-xs text-gray-400">
          {pick(th, "เทียบ 30 วันก่อน", "vs prev 30 days")}
        </span>
      </div>

      {/* The chart */}
      <div className="mt-3 h-[160px] w-full">
        {hasActivity ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="couponPerfGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={ACCENT} stopOpacity={0.28} />
                  <stop offset="95%" stopColor={ACCENT} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                tickFormatter={shortDate}
                tick={{ fontSize: 10, fill: "#9ca3af" }}
                interval="preserveStartEnd"
                minTickGap={24}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 10, fill: "#9ca3af" }}
                width={44}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: number) =>
                  metric === "revenue" && v >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`
                }
              />
              <Tooltip
                cursor={{ stroke: ACCENT, strokeWidth: 1, strokeDasharray: "3 3" }}
                labelFormatter={(label: string) => shortDate(String(label))}
                formatter={(value) => {
                  const n = Number(value) || 0;
                  return [
                    metric === "revenue" ? money(n) : n.toLocaleString(),
                    metric === "revenue"
                      ? pick(th, "ยอดขาย", "Revenue")
                      : pick(th, "ครั้งที่ใช้", "Uses"),
                  ];
                }}
                contentStyle={{
                  borderRadius: 10,
                  border: "1px solid rgba(0,0,0,0.06)",
                  fontSize: 12,
                  padding: "6px 10px",
                }}
              />
              <Area
                type="monotone"
                dataKey={metric}
                stroke={ACCENT}
                strokeWidth={2}
                fill="url(#couponPerfGrad)"
                dot={false}
                activeDot={{ r: 3 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
            <Ticket className="h-5 w-5 text-gray-300" />
            <p className="text-xs text-gray-400">
              {pick(th, "ยังไม่มีการใช้งานคูปอง", "No coupon usage yet")}
            </p>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="my-3 h-px bg-border/60" />

      {/* Stat rail */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {/* Uses left */}
        <div className="flex items-center gap-2">
          <Ticket className="h-4 w-4 shrink-0 text-gray-400" />
          <div className="min-w-0">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {pick(th, "ใช้ไปแล้ว", "Redeemed")}
            </div>
            <div className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
              {redemptions.toLocaleString()}
              <span className="ml-1 text-xs font-normal text-gray-400">
                {usesLeft != null
                  ? pick(th, `· เหลือ ${usesLeft.toLocaleString()}`, `· ${usesLeft.toLocaleString()} left`)
                  : pick(th, "· ไม่จำกัด", "· unlimited")}
              </span>
            </div>
          </div>
        </div>

        {/* Avg order */}
        <div className="flex items-center gap-2">
          <Receipt className="h-4 w-4 shrink-0 text-gray-400" />
          <div className="min-w-0">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {pick(th, "ยอดเฉลี่ยต่อบิล", "Avg order")}
            </div>
            <div className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
              {money(avgOrder)}
            </div>
          </div>
        </div>

        {/* New vs repeat split bar */}
        <div className="min-w-0">
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {pick(th, "ลูกค้า", "Customers")}
            </span>
            <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
              {cust.total.toLocaleString()}
            </span>
          </div>
          <div className="mt-1.5 flex h-2 w-full overflow-hidden rounded-full bg-muted dark:bg-white/[0.08]">
            <div className="bg-sky-500" style={{ width: `${newPct}%` }} />
            <div className="bg-gray-300 dark:bg-white/25" style={{ width: `${repeatPct}%` }} />
          </div>
          <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400">
            <span className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-sky-500" />
              {pick(th, "ใหม่", "New")} {cust.new.toLocaleString()}
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-gray-300 dark:bg-white/25" />
              {pick(th, "เก่า", "Repeat")} {cust.repeat.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {hasActivity && (
        <>
          {/* Insight callouts: new-customer value + ROI/perk */}
          <div className="my-3 h-px bg-border/60" />
          <InsightCallouts
            th={th}
            money={money}
            newRevenue={cust.new_revenue}
            newRevenuePct={cust.new_revenue_pct}
            roi={roi}
            revenue={revenue}
            avgOrder={avgOrder}
          />

          {/* Top products + redemption timing */}
          <div className="my-3 h-px bg-border/60" />
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <TopProducts th={th} items={topProducts} />
            <TimingBars th={th} timing={timing} />
          </div>
        </>
      )}
    </div>
  );
}
